import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import moment from "moment-timezone";
import { toast } from "sonner";
import { TriangleAlert } from "lucide-react";
import Dialog from "../../../../../components/shared/Dialog";
import Button from "../../../../../components/shared/Button";
import { useScheduleContext } from "../context/useScheduleContext";
import { getOrderedWeekdays } from "../../helpers/getOrderedWeekdays";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import useApi from "../../../../../utils/api";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { formatTimeSheetParams } from "../../../../TimeSheet/api/helpers";
import { getErrorMessage } from "../../../../../utils/helpers/errors";
import { withLoadingToast } from "../../helpers/withLoadingToast";

type DeleteConfirmationDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    message: string;
    onConfirm: () => void;
    isPending?: boolean;
};

function DeleteConfirmationDialog({ isOpen, onClose, message, onConfirm, isPending }: DeleteConfirmationDialogProps) {
    const { t } = useTranslation();

    return (
        <Dialog open={isOpen} close={onClose}>
            <div className="tw-flex tw-flex-col tw-gap-7 tw-items-center tw-justify-center tw-mb-7">
                <TriangleAlert size={50} className="tw-text-destructive" />
                <h1 className="tw-text-center">{message}</h1>
            </div>
            <div className="tw-flex tw-justify-end tw-my-2 tw-gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    disabled={isPending}
                >
                    {t("cancel")}
                </Button>
                <Button
                    type="button"
                    variant="destructive"
                    onClick={onConfirm}
                    disabled={isPending}
                    loading={isPending}
                >
                    {t("delete")}
                </Button>
            </div>
        </Dialog>
    );
}

export const useDeleteModal = () => {
    const { t } = useTranslation();
    const { authDelete } = useApi();
    const [filters] = useFilters();
    const { visible, open, close, selected } = useVisible();
    const { dispatch, type, selectedEvents, resetCount, clearSelectedEvents, calendarEvents, templates, refetchEventStatistics } = useScheduleContext();
    const isTeamScheduler = ["team", "template", "profile"].includes(type);
    const orderedWeekdays = getOrderedWeekdays();
    const firstTemplateId = useMemo(() => templates?.[0]?.id, [templates]);

    const onSuccess = useCallback(({ response, type, date, ids, moreModal, unassignedIds }: any) => {
        close();
        resetCount?.();
        refetchEventStatistics();
        clearSelectedEvents();
        if (filters?.mode === "month" && type === "cell") {
            toast.success(t("event-deleted-successfully"));
            if (calendarEvents?.[date]?.total > 3 && !moreModal) {
                dispatch({ type: "refetch-events" });
            }
            const type = moreModal ? "delete-more-modal-event" : "delete-calendar-event";
            dispatch({ type, payload: { id: response?.schedule?.id, date } });
        } else if (type === "bulk-unassign" || type === "cell-unassign") {
            ids.forEach((id: string) => dispatch({ type: "delete-unassigned-event", payload: { id } }))
        } else if (response?.schedule?.id) {
            toast.success(t("event-deleted-successfully"));
            const type = response?.type === "cell-unassign" ? "delete-unassigned-event" : "delete-event";
            dispatch({ type, payload: { id: response?.schedule?.id } });
        } else {
            dispatch({ type: "refetch-events" });
            const data = response?.filter(({ status }: { status: boolean }) => status);
            const count = (data?.length || 0) + (unassignedIds?.length || 0);
            toast.success(t("events-deleted-successfully", { count }));
        }
        unassignedIds?.forEach((id: string) => dispatch({ type: "delete-unassigned-event", payload: { id } }));
        dispatch({ type: "selected-cell-or-event" });
    }, [close, dispatch, resetCount, refetchEventStatistics, filters?.mode, clearSelectedEvents, calendarEvents, t]);

    const cellMutation = useMutation({
        ...withLoadingToast(t("deleting")),
        mutationFn: async ({ id, type, date, moreModal, isTemplate }: any) => {
            let endpoint = isTeamScheduler ? `/schedules/v2/${id}` : `/my/schedules/v2/${id}`;

            if (isTemplate) {
                endpoint = isTeamScheduler
                    ? `/shift-templates/v2/${filters?.template?.id || firstTemplateId}/${id}`
                    : `/my/shift-templates/v2/${filters?.template?.id || firstTemplateId}/${id}`;
            }
            if (type === "cell-unassign") {
                endpoint = `/unassigned-shifts/${id}`;
            }
            const response = await authDelete(endpoint);
            return { response, type, ids: [id], date, moreModal };
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    const userMutation = useMutation({
        ...withLoadingToast(t("deleting")),
        mutationFn: async ({ params, userId, isTemplate }: any) => {
            let endpoint = isTeamScheduler ? `/schedules/v2/user/${userId}` : "/my/schedules/v2/user";
            if (isTemplate) {
                endpoint = isTeamScheduler
                    ? `/shift-templates/v2/${filters?.template?.id || firstTemplateId}/user/${userId}`
                    : `/my/shift-templates/v2/${filters?.template?.id || firstTemplateId}/user`;
            }
            const response = await authDelete(endpoint, { data: params });
            return { response, type: "row" };
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    const bulkMutation = useMutation({
        ...withLoadingToast(t("deleting")),
        mutationFn: async ({ data, type, moreModal, isTemplate, unassignedIds, hasEvents }: any) => {
            let endpoint = isTeamScheduler ? "/schedules/v2/bulk" : "/my/schedules/v2/bulk";
            if (isTemplate) {
                endpoint = isTeamScheduler
                    ? `/shift-templates/v2/${filters?.template?.id || firstTemplateId}/bulk`
                    : `/my/shift-templates/v2/${filters?.template?.id || firstTemplateId}/bulk`;
            }
            if (type === "bulk-unassign") {
                endpoint = `/unassigned-shifts/bulk`;
            }
            // a day cell can hold both assigned events and unassigned shifts, delete them together
            const [response] = await Promise.all([
                hasEvents === false ? undefined : authDelete(endpoint, { data }),
                ...(unassignedIds?.length > 0
                    ? [authDelete("/unassigned-shifts/bulk", { data: { ...data, ids: unassignedIds } })]
                    : [])
            ]);
            return { response, type, ids: data?.ids, moreModal, unassignedIds };
        },
        onSuccess,
        onError: (err) => getErrorMessage(err, t),
    });

    const onConfirm = useCallback(() => {
        let from = filters?.from;
        let to = filters?.to;
        if (filters?.mode === "month") {
            from = moment(filters?.from).startOf("week").format("YYYY-MM-DD");
            to = moment(filters?.to).endOf("week").format("YYYY-MM-DD");
        }

        if (selected?.type === "bulk-day" && !selected?.isTemplate) {
            from = moment(selected?.day).format("YYYY-MM-DD");
            to = moment(selected?.day).format("YYYY-MM-DD");
        }
        const params: any = {
            from,
            to,
            filters: {
                pageIndex: Number(filters?.page) - 1,
                pageSize: filters?.pageSize,
                ...formatTimeSheetParams({
                    filters: filters?.filters,
                    from,
                    to,
                }),
                sort: filters?.sort
            }
        }

        if (selected?.type === "bulk-day" && selected?.isTemplate) {
            params.day = orderedWeekdays[moment(selected?.day).weekday()];
            delete params.from;
            delete params.to;
        }

        if (
            ["template", "profile", "my-profile"].includes(type) &&
            filters.day &&
            filters.mode === "day"
        ) {
            params.day = filters.day;
        }

        if (selected?.type === "bulk-day" || (selected?.type === "bulk" && filters?.mode === "month")) {
            delete params.filters.pageIndex;
            delete params.filters.pageSize;
        }

        if (selected?.type === "cell" || selected?.type === "cell-unassign") {
            cellMutation.mutate({
                id: selected?.event?.id,
                type: selected?.type,
                date: selected?.event?.date,
                moreModal: selected?.moreModal,
                isTemplate: selected?.event?.isTemplate
            });
        } else if (selected?.type === "row") {
            userMutation.mutate({
                params, userId: selected?.userId,
                moreModal: selected?.moreModal,
                isTemplate: selected?.isTemplate
            });
        } else if (selected?.type === "day") {

        } else if (selected?.type === "bulk" || selected?.type === "bulk-unassign" || selected?.type === "bulk-day") {
            const data = {
                ...params,
                ids: selected?.ids
            };
            bulkMutation.mutate({
                data,
                type: selected?.type,
                moreModal: selected?.moreModal,
                isTemplate: selected?.isTemplate,
                unassignedIds: selected?.unassignedIds,
                hasEvents: selected?.hasEvents
            });
        }
    }, [filters, selected, type, orderedWeekdays, cellMutation, userMutation, bulkMutation]);

    const message = useMemo(() => {
        if (
            selectedEvents?.length > 1 ||
            selected?.ids?.length > 0 ||
            selected?.unassignedIds?.length > 0 ||
            selected?.type === "bulk-day"
        ) {
            return t("do-you-want-to-delete-events");
        } else {
            return t("do-you-want-to-delete-event");
        }
    }, [selectedEvents?.length, selected, t]);

    const isPending = cellMutation.isPending || userMutation.isPending || bulkMutation.isPending;

    const modal = useMemo(() => {
        return (
            <DeleteConfirmationDialog
                isOpen={visible}
                onClose={close}
                message={message}
                onConfirm={onConfirm}
                isPending={isPending}
            />
        )
    }, [visible, close, message, onConfirm, isPending]);

    return { open, modal };
}
